/**
 * Shared types for map data structures
 */

/** Tile size in pixels */
export const TILE_SIZE = 32;

/** Minimum / maximum map dimensions in tiles */
export const MIN_MAP_SIZE = 10;
export const MAX_MAP_SIZE = 1000;

/**
 * Tile sprite the game treats as water. There is no water flag in the AMD
 * format - both servers and the client derive it from `tileSprite === 19`.
 */
export const WATER_TILE_SPRITE = 19;

/** Tile flags (byte 8 of a tile record) */
export enum TileFlags {
    NONE = 0,
    /** Movement blocked - read by client and server */
    BLOCKED = 0x80,
    /** Teleport trigger - read by client and server */
    TELEPORT = 0x40,
    /** Farmable - read by the server only */
    FARMING = 0x20,
    /** Not read by any known client or server, and unused in all stock maps */
    BUILDABLE = 0x10,
}

/** Boolean tile properties backed by a flag bit */
export type TileFlagKey = 'blocked' | 'teleport' | 'farming' | 'buildable';

/**
 * Single tile data structure
 * Matches the legacy format: 10 bytes per tile
 */
export interface TileData {
    /** Sprite ID for the base tile (e.g., maptiles1 = 0-31) */
    tileSprite: number;
    /** Frame index within the tile sprite */
    tileSpriteFrame: number;
    /** Sprite ID for the object layer (trees, objects) */
    objectSprite: number;
    /** Frame index within the object sprite */
    objectSpriteFrame: number;
    /** Whether movement is blocked */
    blocked: boolean;
    /** Whether this tile is a teleport point */
    teleport: boolean;
    /** Whether this tile is farmable */
    farming: boolean;
    /** Whether building is allowed */
    buildable: boolean;
    /**
     * Byte 9 of the tile record. Nothing reads it, but stock maps contain
     * uninitialized junk there (0xCD etc.), so it is carried through untouched
     * to keep saves from rewriting bytes the editor doesn't own.
     */
    reserved: number;
}

/**
 * Create an empty tile with default values
 */
export function createEmptyTile(): TileData {
    return {
        tileSprite: 0,
        tileSpriteFrame: 0,
        objectSprite: 0,
        objectSpriteFrame: 0,
        blocked: false,
        teleport: false,
        farming: false,
        buildable: false,
        reserved: 0,
    };
}

/** Whether the game treats this tile as water */
export function isWaterTile(tile: { tileSprite: number }): boolean {
    return tile.tileSprite === WATER_TILE_SPRITE;
}

/**
 * Map document containing all tile data
 */
export interface MapDocument {
    /** Map width in tiles */
    width: number;
    /** Map height in tiles */
    height: number;
    /** 2D array of tile data [x][y] */
    tiles: TileData[][];
    /** Original filename (if loaded from file) */
    filename?: string;
    /** Whether the map has unsaved changes */
    dirty: boolean;
    /** Header bytes as loaded, written back verbatim on save */
    rawHeader?: Uint8Array;
}

/**
 * Create a new empty map with the specified dimensions
 */
export function createEmptyMap(width: number, height: number): MapDocument {
    const tiles: TileData[][] = [];
    for (let x = 0; x < width; x++) {
        tiles[x] = [];
        for (let y = 0; y < height; y++) {
            tiles[x][y] = createEmptyTile();
        }
    }
    return {
        width,
        height,
        tiles,
        dirty: false,
    };
}

/**
 * A rectangular block of frames in a tile sprite that repeats across the map.
 *
 * Stock maps never paint terrain with a single frame: the frame is chosen by
 * map position (block origin + x % width, y % height), so the block reads as
 * one continuous texture. Frames are laid out `columns` per row in the sprite.
 */
export interface TilePattern {
    tileSprite: number;
    /** Frames per row in the sprite's frame table */
    columns: number;
    /** Top-left frame of the block, as column/row in the frame table */
    originCol: number;
    originRow: number;
    /** Block size in tiles */
    width: number;
    height: number;
}

/** Frame a pattern places at a map position */
export function getPatternFrame(pattern: TilePattern, x: number, y: number): number {
    const col = pattern.originCol + (x % pattern.width);
    const row = pattern.originRow + (y % pattern.height);
    return row * pattern.columns + col;
}

/**
 * Terrain presets for new maps
 */
export interface TerrainPreset {
    name: string;
    pattern: TilePattern;
}

export const TERRAIN_PRESETS: TerrainPreset[] = [
    // Blocks verified against the sprite sheets and against how stock maps use them
    { name: 'Grass', pattern: { tileSprite: 0, columns: 20, originCol: 0, originRow: 1, width: 6, height: 4 } },
    { name: 'Dirt', pattern: { tileSprite: 330, columns: 20, originCol: 13, originRow: 0, width: 6, height: 4 } },
    { name: 'Cobblestone', pattern: { tileSprite: 330, columns: 20, originCol: 1, originRow: 0, width: 6, height: 4 } },
    { name: 'Dark Stone', pattern: { tileSprite: 300, columns: 20, originCol: 0, originRow: 0, width: 6, height: 4 } },
    { name: 'Water', pattern: { tileSprite: WATER_TILE_SPRITE, columns: 20, originCol: 0, originRow: 1, width: 6, height: 4 } },
    // Sprite 0 frame 0 is the blank tile stock maps use outside the playable area
    { name: 'Empty', pattern: { tileSprite: 0, columns: 20, originCol: 0, originRow: 0, width: 1, height: 1 } },
];
